import Image from "next/image";
import { Download, Sparkles } from "lucide-react";

type WallpaperItem = {
  id: string | number;
  src: string;
  alt: string;
  width?: number;
  height?: number;
  note?: string;
  downloadName?: string;
};

export default function EasterWallpaperGrid({
  eyebrow,
  title,
  description,
  wallpapers,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  wallpapers: WallpaperItem[];
}) {
  return (
    <section className="py-20" id="featured">
      <div className="container">
        <div className="mx-auto max-w-3xl text-center">
          {eyebrow ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-[#d7c6b8] bg-white/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#9a6c47]">
              <Sparkles className="h-3.5 w-3.5" />
              {eyebrow}
            </div>
          ) : null}
          <h2 className="mt-5 text-balance text-4xl font-semibold tracking-[-0.03em] text-[#2e3529] sm:text-5xl">
            {title}
          </h2>
          {description ? (
            <p className="mt-4 text-base leading-7 text-[#5e6657] sm:text-lg">
              {description}
            </p>
          ) : null}
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {wallpapers.map((wallpaper) => {
            const downloadUrl = `/api/wallpaper/download?src=${encodeURIComponent(
              wallpaper.src
            )}&filename=${encodeURIComponent(
              wallpaper.downloadName || `${wallpaper.id}.png`
            )}`;

            return (
              <article
                key={wallpaper.id}
                className="group overflow-hidden rounded-[28px] border border-[#ddcfc2] bg-white/82 shadow-[0_24px_80px_rgba(84,64,45,0.08)] backdrop-blur"
              >
                <div className="relative overflow-hidden bg-[#f7eee5]">
                  <Image
                    src={wallpaper.src}
                    alt={wallpaper.alt}
                    width={wallpaper.width || 1400}
                    height={wallpaper.height || 1800}
                    loading="eager"
                    sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw"
                    className="h-[320px] w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="pointer-events-none absolute inset-x-4 bottom-4 flex items-center justify-between rounded-full border border-white/50 bg-[#fffaf4]/88 px-4 py-2 text-xs font-medium text-[#5c5e58] shadow-[0_10px_30px_rgba(84,64,45,0.08)]">
                    <span>{wallpaper.note || wallpaper.alt}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 px-5 py-5">
                  <div>
                    <p className="text-sm font-semibold text-[#2e3529]">
                      {wallpaper.alt}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.22em] text-[#99836e]">
                      Direct Download
                    </p>
                  </div>
                  <a
                    href={downloadUrl}
                    download={wallpaper.downloadName || `${wallpaper.id}.png`}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#2f5c47] px-4 text-sm font-semibold text-white transition hover:bg-[#264b3a]"
                  >
                    <Download className="h-4 w-4" />
                    Save
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
